import React, { useEffect, useState, useRef, useCallback } from "react";
import iconNews from "../../../assets/imgs/icon-new-post-ai.svg";
import { actions } from "../../../Redux/store";
import {
  articleEvent,
  getAllArticles,
  getNextArticles,
} from "../../../Controllers/ArticlesApi";
import { shortTimeAgo } from "../../../utils/dateUtils";
import ArticleSkeletonList from "../Component/ArticleSkeletonList";
import {
  IonContent,
  IonHeader,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonToolbar,
} from "@ionic/react";
import {
  hapticsImpactLight,
  markdownStyleOverrides,
  newMarkdownStyleOverrides,
} from "../../../lib/utils";
import { useMixpanel } from "../../../Context/MixpanelContext/MixpanelProvider";
import { useArticleClickStore } from "../../../zustandStore/articleClickStore";
import { lineSpinner } from "ldrs";
import NewsChildTabs from "../../Commons/NewsChildTabs/NewsChildTabs";
import rotoFeed from "../../../assets/imgs/roto_feed.svg";
import BottomTabNavigation from "../../../Routes/BottomTabNavigation";
import articalIcon from "../../../assets/imgs/artical-icon.svg";
import timeIcon from "../../../assets/imgs/time_icon.svg";
import Markdown from "markdown-to-jsx";
lineSpinner.register();
interface Article {
  article_id: string;
  image: string;
  date_created: string;
  title: string;
  full_text: string;
  insight_category?: string;
  header_image_url?: string;
}

export default function PreSeasonNew() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [visibleIndex, setVisibleIndex] = useState<number>(-1);
  const [scrollPosition, setScrollPosition] = useState(0);
  const isHeaderCollapsed = scrollPosition > 40; // Threshold for collapsing header
  const previousIndexRef = useRef(-1);
  const isLoadingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const Mixpanel = useMixpanel();
  const { addArticleClick } = useArticleClickStore();

  const formatInsightCategory = (category: string | undefined): string => {
    if (!category) return "Unknown";

    const categoryMap: { [key: string]: string } = {
      all: "All",
      latest_news: "Latest News",
      player_insight: "Player Insights",
      nfl_draft: "2025 Rookies",
      fantasy_analysis: "Fantasy Drafts",
    };

    return categoryMap[category.toLowerCase()] || category;
  };

  const fetchAllArticles = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const response: any = await getAllArticles();
      const allArticles = response?.data || [];
      setArticles(allArticles);

      if (positionSelected.id === 1) {
        // All tab - show all articles sorted by date DESC
        const sortedArticles = [...allArticles].sort(
          (a, b) =>
            new Date(b.date_created).getTime() -
            new Date(a.date_created).getTime(),
        );
        setFilteredArticles(sortedArticles);
        setHasMore(sortedArticles.length > 0);
      } else {
        const categoryKey = positions.find(
          (p) => p.id === positionSelected.id,
        )?.key;
        const filtered = allArticles.filter(
          (article: Article) => article.insight_category === categoryKey,
        );
        // Sort by date (newest first) within the category
        const sortedFiltered = [...filtered].sort(
          (a, b) =>
            new Date(b.date_created).getTime() -
            new Date(a.date_created).getTime(),
        );
        setFilteredArticles(sortedFiltered);
        setHasMore(filtered.length > 0);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while fetching articles",
      );
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };

  const fetchMoreArticles = async (event?: CustomEvent) => {
    if (!filteredArticles.length || isLoadingMore || !hasMore) {
      (event?.target as HTMLIonInfiniteScrollElement)?.complete();
      return;
    }

    setIsLoadingMore(true);
    const lastArticleId =
      filteredArticles[filteredArticles.length - 1]?.article_id;

    try {
      const response: any = await getNextArticles(lastArticleId);

      if (response?.data) {
        let newArticles: Article[] = [];

        if (Array.isArray(response.data)) {
          newArticles = response.data;
        } else if (response.data.article_id) {
          newArticles = [response.data];
        }

        setArticles((prev) => [...prev, ...newArticles]);

        if (positionSelected.id === 1) {
          // All tab - add new articles sorted by date
          if (newArticles.length === 0) {
            setHasMore(false);
          } else {
            // Sort all articles including the new ones
            const updatedArticles = [...articles, ...newArticles].sort(
              (a, b) =>
                new Date(b.date_created).getTime() -
                new Date(a.date_created).getTime(),
            );
            setFilteredArticles(updatedArticles);
          }
        } else {
          const categoryKey = positions.find(
            (p) => p.id === positionSelected.id,
          )?.key;
          const filtered = newArticles.filter(
            (article: Article) => article.insight_category === categoryKey,
          );

          if (filtered.length === 0) {
            setHasMore(false);
          } else {
            // Sort by date (newest first) when adding to filtered articles
            const updatedFiltered = [...filteredArticles, ...filtered].sort(
              (a, b) =>
                new Date(b.date_created).getTime() -
                new Date(a.date_created).getTime(),
            );
            setFilteredArticles(updatedFiltered);
          }
        }

        if (newArticles.length === 0) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Failed to load more articles:", err);
    } finally {
      setIsLoadingMore(false);
      (event?.target as HTMLIonInfiniteScrollElement)?.complete();
    }
  };

  const handleScroll = (e: CustomEvent) => {
    const scrollDetail = e.detail;
    const currentScrollTop = scrollDetail.scrollTop;
    setScrollPosition(currentScrollTop);
  };

  useEffect(() => {
    if (scrollPosition % 20 === 0 && scrollPosition > 0) {
      console.log("Scroll position updated:", scrollPosition);
    }
  }, [scrollPosition]);

  useEffect(() => {
    if (
      previousIndexRef.current !== -1 &&
      previousIndexRef.current !== visibleIndex &&
      filteredArticles[previousIndexRef.current] &&
      visibleIndex > previousIndexRef.current
    ) {
      Mixpanel.trackEvent("scroll_article_feed", {
        name: "User scrolled through article feed",
        article_id: filteredArticles[previousIndexRef.current].article_id,
        article_title: filteredArticles[previousIndexRef.current].title,
      });
      articleEvent(filteredArticles[previousIndexRef.current].article_id, {
        event_type: "Scrolled",
      }).catch((error) => {
        console.error("Failed to track article scroll event:", error);
      });
    }
    previousIndexRef.current = visibleIndex;
  }, [visibleIndex, filteredArticles, Mixpanel]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(
              entry.target.getAttribute("data-index") || "-1",
            );
            if (index !== visibleIndex) {
              hapticsImpactLight();
              setVisibleIndex(index);

              const articleElement = entry.target as HTMLElement;
              if (entry.intersectionRatio > 0.5) {
                articleElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }
          }
        });
      },
      {
        threshold: [1],
        rootMargin: "-10% 0px",
      },
    );

    document.querySelectorAll("[data-index]").forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [filteredArticles, visibleIndex]);

  useEffect(() => {
    fetchAllArticles();
  }, []);
  type PositionItem = {
    id: number;
    name: string;
  };
  const positions = [
    { id: 1, name: "All", key: "all" },
    { id: 2, name: "Latest News", key: "latest_news" },
    { id: 3, name: "Player Insights", key: "player_insight" },
    { id: 4, name: "2025 Rookies", key: "nfl_draft" },
    { id: 5, name: "Fantasy Drafts", key: "fantasy_analysis" },
  ];

  const defaultPosition = { id: 1, name: "All" };

  const [positionSelected, setPosicionSelected] =
    useState<PositionItem>(defaultPosition);
  useEffect(() => {
    if (!isLoading && articles.length > 0) {
      if (positionSelected.id === 1) {
        // All tab - show all articles sorted by date DESC
        const sortedArticles = [...articles].sort(
          (a, b) =>
            new Date(b.date_created).getTime() -
            new Date(a.date_created).getTime(),
        );
        setFilteredArticles(sortedArticles);
      } else {
        const categoryKey = positions.find(
          (p) => p.id === positionSelected.id,
        )?.key;
        const filtered = articles.filter(
          (article) => article.insight_category === categoryKey,
        );
        // Sort by date (newest first) within the category
        const sortedFiltered = [...filtered].sort(
          (a, b) =>
            new Date(b.date_created).getTime() -
            new Date(a.date_created).getTime(),
        );
        setFilteredArticles(sortedFiltered);
      }

      // Reset visible index for active article tracking
      setVisibleIndex(-1);
    }
  }, [positionSelected, articles, isLoading]);

  const handleOpenNewsModal = (news: Article) => {
    Mixpanel.trackEvent("article_clicked", {
      name: "User clicked to view article details",
      article_id: news.article_id,
      article_title: news.title,
    });
    hapticsImpactLight();

    addArticleClick(news.article_id);

    actions.modal.openNewsDetailsModal({ article_id: news.article_id });

    articleEvent(news.article_id, { event_type: "Opened" }).catch((error) => {
      console.error("Failed to track article event:", error);
    });
  };
  const handleRefresh = async (event: CustomEvent<any>) => {
    try {
      fetchAllArticles();
    } catch (err) {
      console.error("Error refreshing articles:", err);
      setError("Failed to refresh. Please try again.");
    } finally {
      setTimeout(() => {
        event.detail.complete();
      }, 300);
    }
  };

  const handleSelectPosition = useCallback(
    (item: PositionItem) => {
      // If selecting the same tab, no need to change anything
      if (positionSelected.id === item.id) return;

      setPosicionSelected(item);
      setHasMore(true);

      // Check if we need to scroll to top
      if (contentRef.current) {
        contentRef.current.getScrollElement().then((scrollElement) => {
          contentRef.current?.scrollToTop(500);
        });
      }
    },
    [positionSelected],
  );

  return (
    <IonPage className="custom-page">
      <IonHeader
        collapse="fade"
        className={`${isHeaderCollapsed ? "collapsed-header" : "custom-header"}`}
      >
        <IonToolbar className="custom-toolbar">
          <div
            className={`title-container flex items-center justify-center pt-3 pb-5 gap-3 overflow-hidden`}
            style={{
              maxHeight: isHeaderCollapsed ? "0" : "100px",
              margin: isHeaderCollapsed ? "0" : undefined,
              paddingTop: isHeaderCollapsed ? "0" : undefined,
              paddingBottom: isHeaderCollapsed ? "0" : undefined,
              transform: isHeaderCollapsed
                ? "translateY(-100%)"
                : "translateY(0)",
              transition: "all 300ms ease-in-out",
              position: "relative",
              visibility: isHeaderCollapsed ? "hidden" : "visible",
            }}
          >
            <img src={rotoFeed} alt="rotoFeed" />
            <span className="text-[26px] text-white font-semibold">
              RotoFeed
            </span>
          </div>
          <div className={`py-3 transition-all duration-300`}>
            <NewsChildTabs
              tabItems={positions}
              tabCustomStyle="w-fit h-[34px] shrink-0"
              onTabSelect={handleSelectPosition}
              InicialselectedTab={positionSelected}
            />
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent
        scrollY
        style={{ "--background": "#000513" }}
        ref={contentRef}
        onIonScroll={handleScroll}
        scrollEvents={true}
      >
        <IonRefresher mode="md" slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <div
          className="flex flex-col items-center w-full scroll-container px-[16px] py-[10px]"
          ref={containerRef}
          style={{
            scrollBehavior: "smooth",
            scrollSnapType: "y mandatory",
          }}
        >
          {isLoading ? (
            <ArticleSkeletonList count={2} />
          ) : error ? (
            <div className="p-4 mt-4 text-center text-red-500">
              <p className="font-medium">Failed to load articles: {error}</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="p-4 mt-4 text-center text-white/80">
              <p className="font-medium">No articles found in this category</p>
            </div>
          ) : (
            <>
              {filteredArticles?.map((news, index) => (
                <div
                  key={index}
                  data-index={index}
                  className={`relative flex w-full justify-center cursor-pointer transition-all duration-300 ${
                    visibleIndex === index
                      ? "opacity-100 scale-100"
                      : "opacity-30 scale-95"
                  }`}
                  onClick={() => handleOpenNewsModal(news)}
                  style={{
                    scrollSnapAlign: "center",
                    scrollSnapStop: "always",
                    minHeight: "530px",
                    padding: "5px 0",
                  }}
                >
                  <div className="relative w-full max-w-[576px]  h-[530px]">
                    <img
                      src={news?.header_image_url || ""}
                      alt="news"
                      className="rounded-[12px] w-full h-full object-cover"
                    />

                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black via-black/90 to-transparent pt-12 rounded-b-[12px]">
                      <div className="flex flex-col space-y-2">
                        <p className="text-white font-bold text-[20px] leading-[26px] text-shadow-sm">
                          {news.title}
                        </p>
                        <div className="flex justify-between items-end">
                          <article className="prose prose-slate max-w-none w-full article-details text-white/80 font-medium text-[13px] leading-[20px] flex-1 line-clamp-2 text-ellipsis overflow-hidden text-shadow-sm">
                            <Markdown
                              options={{
                                overrides: newMarkdownStyleOverrides,
                              }}
                            >
                              {news.full_text}
                            </Markdown>
                          </article>
                          <span
                            className="text-white/95 cursor-pointer flex font-semibold whitespace-nowrap text-[13px]"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenNewsModal(news);
                            }}
                          >
                            See More
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="bg-white/10 rounded-[12px] py-[8px] px-[10px] flex items-center gap-2 bg-blur-[5.80px]">
                            <img src={articalIcon} alt="articalIcon" />

                            <span className="text-[11px] font-medium text-white/80">
                              {formatInsightCategory(news?.insight_category)}
                            </span>
                          </div>
                          <div className="bg-white/10 rounded-[12px] py-[8px] px-[10px] flex items-center gap-[6px] bg-blur-[5.80px]">
                            <img
                              src={timeIcon}
                              alt="timeIcon"
                              className="w-[12px] h-[12px]"
                            />

                            <span className="text-[11px] font-medium text-white/80">
                              {shortTimeAgo(news.date_created)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <IonInfiniteScroll
                onIonInfinite={fetchMoreArticles}
                disabled={!hasMore}
                threshold="100px"
              >
                <IonInfiniteScrollContent>
                  <div className="flex justify-center items-center py-4">
                    {isLoadingMore && (
                      <div className="spinner-container flex flex-col items-center space-y-2">
                        <div className="spinner w-6 h-6 border-4 border-t-4 border-gray-200 border-t-[#282FFF] rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                </IonInfiniteScrollContent>
              </IonInfiniteScroll>
            </>
          )}
        </div>
      </IonContent>
      <BottomTabNavigation />
    </IonPage>
  );
}
